import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import type {
  ManualCheck,
  ManualCheckListFilters,
} from '../../types/manualCheck';
import { ManualCheckForm } from './ManualCheckForm';
import { ManualChecksTable } from './ManualChecksTable';
import { ManualChecksSalesmanView } from './ManualChecksSalesmanView';
import { ManualCheckSearchTab } from './ManualCheckSearchTab';

interface Salesman {
  id: number;
  name: string;
}

type TabKey = 'recent' | 'search';

const TAB_QUERY_KEY = 'tab';

const isTabKey = (value: string | null): value is TabKey =>
  value === 'recent' || value === 'search';

/**
 * R16 Task A + R17 Task G: Manual Check management page.
 *
 * Two tabs:
 *  - Recent Entries — entry form, filter chips, master log, per-salesman view
 *  - Search — dedicated check-history lookup (date/firm/salesman/amount range)
 *
 * Tab state is persisted to `?tab=search` so a URL shared with a colleague
 * opens directly on the search view. Delegates its two heavy tabs to
 * dedicated subcomponents (ManualCheckSearchTab + the inline Recent tab)
 * so this page stays under the 400-LOC per-file cap.
 */
export const ManualChecks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(TAB_QUERY_KEY);
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'recent';

  const setActiveTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'recent') {
        params.delete(TAB_QUERY_KEY);
      } else {
        params.set(TAB_QUERY_KEY, next);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [salesmen, setSalesmen] = useState<Salesman[]>([]);
  const [firms, setFirms] = useState<string[]>([]);
  const [checks, setChecks] = useState<ManualCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ManualCheck | null>(null);

  // Recent-tab filter chips.
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterSalesman, setFilterSalesman] = useState<number | ''>('');
  const [filterFirm, setFilterFirm] = useState<string>('');

  const [detailSalesman, setDetailSalesman] = useState<Salesman | null>(null);

  const loadSalesmen = useCallback(async () => {
    try {
      const resp = await api.getEmployees({ is_active: true, per_page: 200 });
      const data: any[] = resp.data ?? [];
      const rows: Salesman[] = data
        .map((u) => ({ id: u.id, name: u.name || u.email }))
        .filter((s) => s.id && s.name);
      setSalesmen(rows);
    } catch (err) {
      // non-fatal
      console.error('Failed to load salesmen', err);
    }
  }, []);

  const loadFirms = useCallback(async () => {
    try {
      const resp = await api.getManualCheckFirms();
      setFirms(resp.data ?? []);
    } catch (err) {
      console.error('Failed to load firm suggestions', err);
    }
  }, []);

  const loadChecks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: ManualCheckListFilters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        salesman_user_id: filterSalesman === '' ? undefined : Number(filterSalesman),
        firm_name: filterFirm || undefined,
        per_page: 100,
      };
      const resp = await api.getManualChecks(filters);
      setChecks(resp.data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load checks');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterSalesman, filterFirm]);

  useEffect(() => {
    loadSalesmen();
    loadFirms();
  }, [loadSalesmen, loadFirms]);

  useEffect(() => {
    loadChecks();
  }, [loadChecks]);

  const handleSaved = () => {
    setEditing(null);
    loadChecks();
    loadFirms();
  };

  const handleDelete = async (check: ManualCheck) => {
    if (!window.confirm(`Delete manual check for ${check.firm_name} on ${check.check_date}?`)) {
      return;
    }
    try {
      await api.deleteManualCheck(check.id);
      loadChecks();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Delete failed');
    }
  };

  /**
   * Search-tab edit hook: switch to Recent Entries, populate the edit
   * form, and scroll to the top so the form is visible.
   */
  const handleEditFromSearch = useCallback(
    (check: ManualCheck) => {
      setEditing(check);
      setActiveTab('recent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setActiveTab],
  );

  const tabButtonClass = (tab: TabKey) =>
    `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      activeTab === tab
        ? 'border-blue-600 text-blue-700'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  const tabs = useMemo(
    () => [
      { key: 'recent' as TabKey, label: 'Recent Entries' },
      { key: 'search' as TabKey, label: 'Search' },
    ],
    [],
  );

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual Checks</h1>
          <p className="text-sm text-gray-600">
            Record physical checks that live outside the CSV pipeline. They roll
            up alongside trade commissions on daily blotter PDFs and monthly
            reports.
          </p>
        </div>
      </header>

      <div className="border-b border-gray-200" role="tablist" aria-label="Manual Checks views">
        <nav className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={tabButtonClass(tab.key)}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'recent' ? (
        <div className="space-y-6" role="tabpanel">
          <ManualCheckForm
            salesmen={salesmen}
            firmSuggestions={firms}
            initial={editing}
            onSaved={handleSaved}
            onCancel={editing ? () => setEditing(null) : undefined}
          />

          {/* Filter chips */}
          <section className="bg-white shadow rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="text-sm">
              <span className="text-gray-700">Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-700">End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-700">Salesman</span>
              <select
                value={filterSalesman}
                onChange={(e) =>
                  setFilterSalesman(e.target.value ? Number(e.target.value) : '')
                }
                className="mt-1 w-full border rounded px-2 py-1"
              >
                <option value="">All salesmen</option>
                {salesmen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-gray-700">Firm</span>
              <input
                type="text"
                list="manual-check-firms-filter"
                value={filterFirm}
                onChange={(e) => setFilterFirm(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1"
                placeholder="e.g. Acme"
              />
              <datalist id="manual-check-firms-filter">
                {firms.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </label>
          </section>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <section className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Entries</h2>
              {loading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
            <ManualChecksTable
              checks={checks}
              onEdit={(c) => {
                setEditing(c);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onDelete={handleDelete}
            />
          </section>

          {/* Per-salesman tier view */}
          <section className="bg-white shadow rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Per-Salesman Running Totals
              </h2>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={detailSalesman?.id ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const s = salesmen.find((x) => x.id === id) ?? null;
                  setDetailSalesman(s);
                }}
              >
                <option value="">-- select salesman --</option>
                {salesmen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {detailSalesman && (
              <ManualChecksSalesmanView
                salesmanUserId={detailSalesman.id}
                salesmanName={detailSalesman.name}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
              />
            )}
          </section>
        </div>
      ) : (
        <div role="tabpanel">
          <ManualCheckSearchTab
            salesmen={salesmen}
            firms={firms}
            onEditCheck={handleEditFromSearch}
            onDataChanged={() => {
              loadChecks();
              loadFirms();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ManualChecks;
